import React, { useCallback } from "react";

import dynamic from "next/dynamic";

import { ChartFormatEnum } from "@/enum/chart";
import { MainPageTabs } from "@/enum/common";
import { GenerationEnum, InvestmentEnum } from "@/enum/filters";
import TableChart, { ITableColumns } from "@/features/explore/charts/TableChart";
import TextChart from "@/features/explore/charts/TextChart";
import GenderIndexMobileView from "@/features/explore/GenderIndexMobileView";
import { rankNumbers } from "@/helpers/common";
import useCheckMobileScreen from "@/hooks/useCheckMobileScreen";
import { useFilters } from "@/providers/FiltersProvider";
import { IFilterDataState } from "@/reducers/filterReducer";
import HeatSpotColours from "@/services/HeatSpotColours";
import NoDataContainer from "@/shared/components/NoDataContainer";

const MapContainer = dynamic(() => import("@/features/explore/maps/MapContainer"), { ssr: false });
const DonutChart = dynamic(() => import("@/features/explore/charts/DonutChart"), { ssr: false });
const BarChart = dynamic(() => import("@/features/explore/charts/BarChart"), { ssr: false });
const InvestmentBarChart = dynamic(() => import("@/features/explore/charts/InvestmentBarChart"), { ssr: false });

interface IExploreTab {
    selectedTab: MainPageTabs | undefined;
    searchFilterData: IFilterDataState;
    isLoading: boolean;
    dispatch?: any;
}
function ExploreTab({ selectedTab, searchFilterData, isLoading }: IExploreTab) {
    const isMobile = useCheckMobileScreen();
    const { mainFilters } = useFilters();
    const getGender = useCallback(() => {
        return mainFilters?.["D001"]?.data[0]?.label;
    }, [mainFilters]);

    switch (selectedTab) {
        case MainPageTabs.summary:
        {
            return (
                <div className='flexSpaceBtw textChartSummary'>
                    {
                        searchFilterData.summary?.data?.map((i: any, index: number) => (
                            <TextChart data={i} key={index} isLoading={isLoading} />
                        ))
                    }
                </div>
            );
        }
        case MainPageTabs.regions:
        {
            const getRegion = () => {
                const filterKeys = Object.keys(mainFilters);
                if (filterKeys.includes("LL001")) {
                    return "UK nations";
                } else if (filterKeys.includes("LL002")) {
                    return "Regions";
                } else if (filterKeys.includes("LL003")) {
                    return "LEPs";
                } else if (filterKeys.includes("LL004")) {
                    return "Local authorities";
                } else if (filterKeys.includes("LL005")) {
                    return "Constituencies";
                } else {
                    return "Region Name";
                }
            };

            const rankedNumbersArray = searchFilterData?.regions?.data?.map(location => (location.gender_percentage))
                ? rankNumbers(searchFilterData.regions.data.map(location => (location.gender_percentage))) : [];
            let row = searchFilterData.regions.data || [];
            row = row?.map((i, key) => {
                const multiplier = HeatSpotColours.gradientSteps / rankedNumbersArray.length;
                let colourNumber = 0;

                if (rankedNumbersArray.length <= 1 || (rankedNumbersArray.length === 2 && rankedNumbersArray[key] == 2)) {
                    colourNumber = 9;
                } else if (rankedNumbersArray.length === 2 && rankedNumbersArray[key] == 1) {
                    colourNumber = 0;
                } else {
                    colourNumber = Math.round((((rankedNumbersArray[key]) - 1) * multiplier) - 1);
                }
                return {
                    ...i,
                    colourNumber,
                };

            });
            const columns: ITableColumns[] = [{
                name: getRegion(),
                key: "name",
                format: ChartFormatEnum.text,
                sortable: true,
                type: "string",
                render: (value: any, all: any) => {
                    return (
                        <div
                            className="tableHeatSpotItemCont">
                            <div
                                className="tableHeatSpotItem"
                                style={{
                                    background: HeatSpotColours.getHeatSpotColourByNumber(all.colourNumber),
                                }} />
                            {value}
                        </div>
                    );
                },
            },
            {
                name: "Total companies",
                key: "company_count",
                format: ChartFormatEnum.number,
                sortable: true,
                type: "number",
            },
            {
                name: `${getGender()}-led`,
                key: "gender_count",
                format: ChartFormatEnum.number,
                sortable: true,
                type: "number",
            },
            {
                name: `% ${getGender()}-led`,
                key: "gender_percentage",
                format: ChartFormatEnum.percent,
                sortable: true,
                type: "number",
            }];

            if (row?.length === 0 && !isLoading) {
                return <NoDataContainer />;
            }
            if (isMobile) {
                return (<GenderIndexMobileView
                    listUi={<TableChart
                        row={row}
                        columns={columns.filter((item, index) => index !== 2)}
                        defaultSort={{
                            direction: "DESC",
                            sortItem: "gender_percentage",
                        }} />}
                    mapUi={
                        <div className='chartContainer'>

                            <MapContainer
                                regionData={searchFilterData.regions}
                                gender={getGender()} />
                            {/* <ColourKey /> */}
                        </div>
                    }
                />
                );
            }
            return (
                <div className='tableChartWrapper'>
                    <TableChart
                        row={row}
                        columns={columns}
                        defaultSort={{
                            direction: "DESC",
                            sortItem: "gender_percentage",
                        }} />
                    <div className='chartContainer'>

                        <MapContainer
                            regionData={searchFilterData.regions}
                            gender={getGender()} />
                    </div>
                </div>
            );
        }
        case MainPageTabs.sectors:
        {
            const columns: ITableColumns[] = [{
                name: "Sector Name",
                key: "name",
                format: ChartFormatEnum.text,
                sortable: true,
                type: "string",
            },
            {
                name: "Total companies",
                key: "company_count",
                format: ChartFormatEnum.number,
                sortable: true,
                type: "number",
            },
            {
                name: `${getGender()}-led`,
                key: "gender_count",
                format: ChartFormatEnum.number,
                sortable: true,
                type: "number",
            },
            {
                name: `% ${getGender()}-led`,
                key: "gender_percentage",
                format: ChartFormatEnum.percent,
                sortable: true,
                type: "number",
            }];

            const row = searchFilterData.sectors.data;
            const chartData = searchFilterData.sectors.data?.map((i) => ({
                name: i.name,
                data: i.gender_percentage,
            })).slice(0, 5);

            if (row?.length === 0 && !isLoading) {
                return <NoDataContainer />;
            }
            if (isMobile) {
                return (<GenderIndexMobileView
                    listUi={<TableChart
                        row={row}
                        columns={columns.filter((item, index) => index !== 2)}
                        defaultSort={{
                            direction: "DESC",
                            sortItem: "gender_percentage",
                        }} />}
                    mapUi={<div className='chartContainer'>

                        <DonutChart
                            data={chartData}
                            height={500}
                            title={`Top 5 sectors by ${getGender()}-led %`}
                        />
                    </div>} />
                );
            }
            return (
                <div className='tableChartWrapper'>
                    <TableChart
                        row={row}
                        columns={columns}
                        defaultSort={{
                            direction: "DESC",
                            sortItem: "gender_percentage",
                        }} />
                    <div className='chartContainer'>

                        <DonutChart
                            data={chartData}
                            height={320}
                            title={`Top 5 sectors by ${getGender()}-led %`} />
                    </div>
                </div>
            );
        }
        case MainPageTabs.generations:
        {
            const columns: ITableColumns[] = [{
                name: "Generations",
                key: GenerationEnum.name,
                format: ChartFormatEnum.text,
                sortable: true,
                type: "string",
            },
            {
                name: "Total companies",
                key: GenerationEnum.company_count,
                format: ChartFormatEnum.number,
                sortable: true,
                type: "number",
            },
            {
                name: `${getGender()}-led`,
                key: GenerationEnum.gender_count,
                format: ChartFormatEnum.number,
                sortable: true,
                type: "number",
            },
            {
                name: `${getGender()}-led %`,
                key: GenerationEnum.gender_percentage,
                format: ChartFormatEnum.percent,
                sortable: true,
                type: "number",
            },
            ];
            const row = searchFilterData.generations.data;

            const category = searchFilterData.generations.data?.map((i: any) => i.name);
            const chartArray = [
                {
                    name: `${getGender()}-led`,
                    key: GenerationEnum.gender_count,
                },
                {
                    name: "Total companies",
                    key: GenerationEnum.company_count,
                },
            ];
            const chartData = [];
            if (row && category) {
                for (const c of chartArray) {
                    const _data: any[] = [];
                    category?.forEach(i => {
                        const _r = row?.find(j => j.name === i);
                        if (_r) {
                            _data.push(_r[c.key]);
                        }
                    });
                    chartData.push({
                        name: c.name,
                        data: _data,
                    });
                }
            }
            if (row?.length === 0 && !isLoading) {
                return <NoDataContainer />;
            }
            if (isMobile) {
                return (<GenderIndexMobileView
                    listUi={<TableChart
                        row={row}
                        columns={columns.filter((item, index) => index !== 2)}
                        defaultSort={{
                            direction: "DESC",
                            sortItem: GenerationEnum.gender_percentage,
                        }} />}
                    mapUi={<div className='chartContainer'>
                        <BarChart
                            data={chartData}
                            category={category}
                            height={400}
                            originalData={searchFilterData.generations.data}
                            genderLed={getGender()} />
                    </div>} />
                );
            }
            return (
                <div className='tableChartWrapper'>
                    <TableChart
                        row={row}
                        columns={columns}
                        defaultSort={{
                            direction: "DESC",
                            sortItem: GenerationEnum.gender_percentage,
                        }} />
                    <div className='chartContainer'>
                        <BarChart
                            data={chartData}
                            category={category}
                            originalData={searchFilterData.generations.data}
                            genderLed={getGender()}
                            height={400} />
                    </div>
                </div>
            );
        }
        case MainPageTabs.investment:
        {

            const columns: ITableColumns[] = [{
                name: "Investment type",
                key: InvestmentEnum.name,
                format: ChartFormatEnum.text,
                sortable: true,
                type: "string",
            },
            {
                name: "Total investments",
                key: InvestmentEnum.total_investments,
                format: ChartFormatEnum.number,
                sortable: true,
                type: "number",
            },
            {
                name: `${getGender()}-led`,
                key: "gender_count",
                format: ChartFormatEnum.number,
                sortable: true,
                type: "number",
            },
            {
                name: `${getGender()}-led %`,
                key: "gender_percentage",
                format: ChartFormatEnum.percent,
                sortable: true,
                type: "number",
            }];
            const row = searchFilterData.investment.data;

            const chartSearchData = searchFilterData.investment.data?.filter(i => i[InvestmentEnum.name].toLowerCase() !== "no investment");

            const category = searchFilterData.investment.data?.filter(i => i[InvestmentEnum.name].toLowerCase() !== "no investment").map((i: any) => i.name);
            const chartArray = [
                {
                    name: `${getGender()}-led investments`,
                    key: InvestmentEnum.gender_count,
                },
                {
                    name: "Total investments",
                    key: InvestmentEnum.total_investments,
                },
            ];
            const chartData = [];
            if (chartSearchData && category) {
                for (const c of chartArray) {
                    const _data: any[] = [];
                    category?.forEach(i => {
                        const _r = chartSearchData?.find(j => j.name === i);
                        if (_r) {
                            _data.push(_r[c.key]);
                        }
                    });
                    chartData.push({
                        name: c.name,
                        data: _data,
                    });
                }
            }

            if (row?.length === 0 && !isLoading) {
                return <NoDataContainer />;
            }
            if (isMobile) {
                return (<GenderIndexMobileView
                    listUi={<TableChart
                        row={row}
                        columns={columns.filter((item, index) => index !== 2)}
                        defaultSort={{
                            direction: "DESC",
                            sortItem: InvestmentEnum.gender_percentage,
                        }} />}
                    mapUi={<div className='chartContainer'>
                        <InvestmentBarChart
                            data={chartData}
                            category={category}
                            originalData={chartSearchData}
                            genderLed={getGender()}
                            height={400} />
                        {/* <WeightChart data={WeightChartData} title={`${getGender()}-led investment comparison`} gender={getGender()?.toLowerCase()} /> */}
                    </div>}
                />
                );
            }
            return (
                <div className='tableChartWrapper'>
                    <TableChart
                        row={row}
                        columns={columns}
                        defaultSort={{
                            direction: "DESC",
                            sortItem: InvestmentEnum.gender_percentage,
                        }} />
                    <div className='chartContainer'>

                        <InvestmentBarChart
                            data={chartData}
                            category={category}
                            originalData={chartSearchData}
                            genderLed={getGender()}
                            height={400} />
                        {/* <WeightChart data={WeightChartData} title={`${getGender()}-led investment comparison`} gender={getGender()?.toLowerCase()} /> */}
                    </div>
                </div>
            );
        }
        default:
            return null;
    }
}

export default ExploreTab;
